import { LoadFailureClass } from './sheet-load-trace.util';

/**
 * REFACTORED-DIAGNOSTIC-CODES-FLAG — TEMPORARY. Delete this whole file once
 * production is cleared to receive the refactored diagnostics and the runtime
 * flag `refactoredDiagnosticCodesEnabled` is removed (see
 * `RuntimeConfig.refactoredDiagnosticCodesEnabled` for the full removal list).
 *
 * ## What this is
 *
 * The three generic conversion-sheet load-failure toasts that existed before
 * commit `d09182b6` (2026-09-03) replaced them with `MBRS-S<n>-<CLASS>` codes.
 * Production runs with the flag OFF and shows these instead, so a sheet failing
 * to load reads as it used to — a general message, no code.
 *
 * They are the three strings `scripts/check-sheet-load-trace.mjs` forbids in any
 * sheet component under `xbrl-form/`. That guard scans only the sheet folders, so
 * this file under `utils/` is the one sanctioned home for them — keep them here,
 * never back in a sheet.
 *
 * ## Why one message per failure CLASS and not per original call site
 *
 * The original messages were hard-coded at ~27 sheets, each choosing by where it
 * caught the error. The classifier now names the cause precisely, so the mapping
 * follows what each original message described (P.O. approved the table,
 * 2026-10-14):
 *
 * | Original message | Failure classes |
 * |---|---|
 * | `Failed to load form schema` | `ASSET`, `PARSE` — the form-layout file |
 * | `Failed to load form. Please refresh the page.` | `BUILD`, `STALL` — the sheet could not be prepared, or never finished |
 * | `Failed to load MBRS data` | `NET` (including a schema request that never completed, i.e. offline — accepted by the P.O.), `SRV`, `RESP`, `PERM` — the conversion data (`REQ` retired 2026-10-14; its 4xx cases are now `RESP`) |
 * | `Failed to load MSIC code options` | `EMPTY` — role 2's MSIC code list was unavailable (a degraded report, not a failed load) |
 *
 * `AUTH` has no entry: it is silent in both modes (see `SILENT_FAILURE_CLASSES`).
 */
export const LEGACY_SHEET_LOAD_FAILURE_COPY: Readonly<
  Record<Exclude<LoadFailureClass, 'AUTH'>, string>
> = {
  ASSET: 'Failed to load form schema',
  PARSE: 'Failed to load form schema',
  BUILD: 'Failed to load form. Please refresh the page.',
  STALL: 'Failed to load form. Please refresh the page.',
  NET: 'Failed to load MBRS data',
  SRV: 'Failed to load MBRS data',
  RESP: 'Failed to load MBRS data',
  // EMPTY is role 2's MSIC code list only (a degraded report), so it keeps the
  // sentence that situation already showed before the codes existed.
  EMPTY: 'Failed to load MSIC code options',
  PERM: 'Failed to load MBRS data',
};

/**
 * The generic sentence for a failed sheet load, or `null` for a class that is
 * never toasted (AUTH).
 */
export function legacySheetLoadFailureMessage(failure: LoadFailureClass): string | null {
  return failure === 'AUTH' ? null : LEGACY_SHEET_LOAD_FAILURE_COPY[failure];
}
